import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { TrendingUp, History, Analytics } from '@mui/icons-material';
import './ForecastModals.css';

const DailyForecastUpdate = ({ open, onClose, onSave }) => {
  const [tomorrowsDate, setTomorrowsDate] = useState('');
  const [currentForecasts, setCurrentForecasts] = useState([]);
  const [revisedForecasts, setRevisedForecasts] = useState({});
  const [revisionReason, setRevisionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forecastHistory, setForecastHistory] = useState([]);
  const [totalChanges, setTotalChanges] = useState(0);

  const revisionReasons = [
    'Cancellations received',
    'New bookings added',
    'Staff availability changed',
    'Seasonal adjustment',
    'Market conditions',
    'Client rescheduling',
    'Other'
  ];

  useEffect(() => {
    if (open) {
      loadTomorrowsForecasts();
    }
  }, [open]);

  const loadTomorrowsForecasts = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Get tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setTomorrowsDate(tomorrowStr);
      
      // Get current forecasts for tomorrow
      const response = await fetch(`/api/forecast/daily-forecast?date=${tomorrowStr}`);
      const data = await response.json();
      
      if (data.success) {
        setCurrentForecasts(data.forecasts);
        
        // Initialize revised forecasts with current values
        const initialRevised = {};
        data.forecasts.forEach(forecast => {
          initialRevised[forecast.staff_member_id] = forecast.latest_forecast || forecast.original_forecast;
        });
        setRevisedForecasts(initialRevised);
        
        // Load revision history
        loadRevisionHistory(tomorrowStr);
      } else {
        setError(data.message || 'Failed to load forecasts');
      }
    } catch (err) {
      setError('Error loading tomorrow\'s forecasts');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRevisionHistory = async (date) => {
    try {
      const response = await fetch(`/api/forecast/revision-history?date=${date}`);
      const data = await response.json();
      
      if (data.success) {
        setForecastHistory(data.revisions);
      }
    } catch (err) {
      console.error('Error loading revision history:', err);
    }
  };

  const handleForecastChange = (staffMemberId, value) => {
    const numericValue = parseFloat(value) || 0;
    setRevisedForecasts(prev => ({
      ...prev,
      [staffMemberId]: numericValue
    }));
    
    // Calculate total changes
    const originalValue = currentForecasts.find(f => f.staff_member_id === staffMemberId)?.latest_forecast || 0;
    const change = numericValue - originalValue;
    
    setTotalChanges(prev => {
      const otherChanges = Object.entries(revisedForecasts)
        .filter(([id]) => parseInt(id) !== staffMemberId)
        .reduce((sum, [id, revised]) => {
          const original = currentForecasts.find(f => f.staff_member_id === parseInt(id))?.latest_forecast || 0;
          return sum + (revised - original);
        }, 0);
      
      return otherChanges + change;
    });
  };

  const handleSave = async () => {
    if (!revisionReason.trim()) {
      setError('Please provide a reason for the forecast revision');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const revisions = Object.entries(revisedForecasts).map(([staffMemberId, revisedAmount]) => {
        const original = currentForecasts.find(f => f.staff_member_id === parseInt(staffMemberId));
        return {
          staff_member_id: parseInt(staffMemberId),
          forecast_date: tomorrowsDate,
          original_forecast: original?.original_forecast || 0,
          latest_forecast: original?.latest_forecast || original?.original_forecast || 0,
          revised_forecast: revisedAmount,
          revision_reason: revisionReason,
          revision_date: new Date().toISOString()
        };
      });

      const response = await fetch('/api/forecast/daily-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: tomorrowsDate,
          revisions: revisions
        }),
      });

      const data = await response.json();

      if (data.success) {
        onSave && onSave(data);
        onClose();
      } else {
        setError(data.message || 'Failed to save forecast revisions');
      }
    } catch (err) {
      setError('Error saving forecast revisions');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTotalCurrent = () => {
    return currentForecasts.reduce((sum, forecast) => 
      sum + (forecast.latest_forecast || forecast.original_forecast), 0
    );
  };

  const getTotalRevised = () => {
    return Object.values(revisedForecasts).reduce((sum, value) => sum + value, 0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="forecast-modal-overlay"
    >
      <DialogTitle className="forecast-modal-title">
        <Box display="flex" alignItems="center" gap={2}>
          <TrendingUp />
          <div>
            <Typography variant="h6">Daily Forecast Update</Typography>
            <Typography variant="body2" color="textSecondary">
              {tomorrowsDate && formatDate(tomorrowsDate)}
            </Typography>
          </div>
        </Box>
      </DialogTitle>

      <DialogContent className="forecast-modal-content">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography>Loading tomorrow's forecasts...</Typography>
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Box className="forecast-summary-card">
                  <Typography variant="body2" color="textSecondary">Current Forecast</Typography>
                  <Typography variant="h6">{formatCurrency(getTotalCurrent())}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="forecast-summary-card">
                  <Typography variant="body2" color="textSecondary">Revised Forecast</Typography>
                  <Typography variant="h6">{formatCurrency(getTotalRevised())}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="forecast-summary-card">
                  <Typography variant="body2" color="textSecondary">Net Change</Typography>
                  <Typography 
                    variant="h6" 
                    color={totalChanges >= 0 ? 'success.main' : 'error.main'}
                  >
                    {totalChanges >= 0 ? '+' : ''}{formatCurrency(totalChanges)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Revision Reason */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Reason for Revision</InputLabel>
              <Select
                value={revisionReason}
                onChange={(e) => setRevisionReason(e.target.value)}
                label="Reason for Revision"
              >
                {revisionReasons.map(reason => (
                  <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Staff Forecasts */}
            <Typography variant="h6" sx={{ mb: 2 }}>Staff Forecast Adjustments</Typography>
            <Grid container spacing={2}>
              {currentForecasts.map((forecast) => {
                const currentAmount = forecast.latest_forecast || forecast.original_forecast;
                const revisedAmount = revisedForecasts[forecast.staff_member_id] || currentAmount;
                const change = revisedAmount - currentAmount;
                
                return (
                  <Grid item xs={12} md={6} key={forecast.staff_member_id}>
                    <Box className="forecast-staff-card">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {forecast.staff_name}
                        </Typography>
                        {change !== 0 && (
                          <Chip
                            label={`${change >= 0 ? '+' : ''}${formatCurrency(change)}`}
                            color={change >= 0 ? 'success' : 'error'}
                            size="small"
                          />
                        )}
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" mb={1}>
                        Current: {formatCurrency(currentAmount)}
                      </Typography>
                      
                      <TextField
                        type="number"
                        label="Revised Forecast"
                        value={revisedAmount}
                        onChange={(e) => handleForecastChange(forecast.staff_member_id, e.target.value)}
                        fullWidth
                        InputProps={{
                          startAdornment: <Typography>£</Typography>
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* Revision History */}
            {forecastHistory.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <History />
                  <Typography variant="h6">Recent Revisions</Typography>
                </Box>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {forecastHistory.map((revision, index) => (
                    <Box key={index} className="forecast-revision-item">
                      <Typography variant="body2">
                        <strong>{revision.staff_name}</strong>: {formatCurrency(revision.previous_forecast)} → {formatCurrency(revision.revised_forecast)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {revision.revision_reason} • {new Date(revision.revision_date).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="forecast-modal-actions">
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !revisionReason.trim()}
          startIcon={<Analytics />}
        >
          {loading ? 'Saving...' : 'Save Forecast Revisions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DailyForecastUpdate;